import React from 'react';
import type { CatalogProperty } from 'src/api/generated';
import type { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { PropertyEditorSectionCard } from '../molecules/property-editor-section-card';
import { PropertyPublicationList } from '../molecules/property-publication-list';
import { PropertySyncOwnershipCard } from '../molecules/property-sync-ownership-card';
import type { usePropertyPublishingPanel } from '../../hooks/use-property-publishing-panel';
import { PropertyPublishingEmptyPrompt } from './property-publishing-empty-prompt';

interface PropertyPublishingPanelBodyProps {
  items: ReturnType<typeof usePropertyPublishingPanel>['items'];
  property: CatalogProperty;
  publications: ReturnType<
    typeof usePropertyPublishingPanel
  >['publications']['publications'];
  sourceProviderId: ReturnType<
    typeof usePropertyPublishingPanel
  >['connection']['sourceProviderId'];
  repairSync: {
    canRepairSync: boolean;
    loading: boolean;
    onRepairSync: () => void | Promise<void>;
  };
  t: ReturnType<typeof useI18n>['t'];
  connection: ReturnType<typeof usePropertyPublishingPanel>['connection'];
}

export const PropertyPublishingPanelBody: React.FC<
  PropertyPublishingPanelBodyProps
> = ({
  connection,
  items,
  property,
  publications,
  sourceProviderId,
  repairSync,
  t,
}) => {
  const shouldShowSyncCard =
    connection.isSourceIntegrationConnected && sourceProviderId != null;
  const shouldShowPublishingCard =
    publications.length > 0 ||
    (connection.isMeliConnected &&
      !connection.isMeliSyncMode &&
      !connection.isSourceIntegrationSyncManaged);
  const shouldShowConnectPrompt =
    !connection.isMeliConnected && !connection.isSourceIntegrationSyncManaged;
  const shouldShowUnsupportedPrompt =
    connection.isMeliConnected &&
    !connection.isMeliSyncMode &&
    items.length === 0;

  return (
    <div className="flex flex-col gap-5">
      {shouldShowSyncCard ? (
        <PropertySyncOwnershipCard
          property={property}
          publications={publications}
          providerId={sourceProviderId}
          repairSync={repairSync}
        />
      ) : null}

      {shouldShowPublishingCard ? (
        <PropertyEditorSectionCard
          title={t('catalog.properties.editor.publishing.title')}
          description={t('catalog.properties.editor.publishing.description')}
        >
          <PropertyPublicationList items={items} />
        </PropertyEditorSectionCard>
      ) : null}

      {!shouldShowPublishingCard && shouldShowConnectPrompt ? (
        <PropertyPublishingEmptyPrompt
          descriptionKey="catalog.properties.editor.publishing.connectDescription"
          titleKey="catalog.properties.editor.publishing.connectTitle"
          t={t}
        />
      ) : null}

      {!shouldShowPublishingCard &&
      !shouldShowConnectPrompt &&
      shouldShowUnsupportedPrompt ? (
        <PropertyPublishingEmptyPrompt
          descriptionKey="catalog.properties.editor.publishing.unsupportedDescription"
          titleKey="catalog.properties.editor.publishing.unsupportedTitle"
          t={t}
        />
      ) : null}
    </div>
  );
};
